import { Group } from "../../../../../../types/groupsAndRolesTypes";
import { createGroupValidationSchema } from "../../../../../../yup/schema/groupsAndRoles/groupValidationSchema";
import {
  AsyncCallArgs,
  GridContainer,
  GridItem,
  handleSubmitValidationFail,
  InputFormField,
  ReloadWatcherObject,
  ScrollableAsyncFormAutocomplete,
  SubmitFormButton,
  useAsyncCall,
  useReactHookForm,
  useReloadWatcherWriter,
} from "@duosoftbg/nacid-components";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Typography } from "@mui/material";
import { getGroupsAutocomplete, saveGroupMainDetails } from "../../../../../../axios/api/services";
import useAppDispatch from "../../../../../../hooks/redux/base/useAppDispatch";
import { closeModal } from "../../../../../../store/redux/slice/ComponentsControl/groupsControl";
import { toast } from "react-toastify";
import React, { useEffect } from "react";

const getDefaultValues = (group): Group => {
  const defaultGroup = { ...group };
  if (!defaultGroup.description) {
    defaultGroup.description = "";
  }
  return defaultGroup;
};

const GroupMainDetailsForm = ({ group, filterFn }) => {
  const { t } = useTranslation();
  const { asyncCall } = useAsyncCall();
  const dispatch = useAppDispatch();
  const { updateReloadWatcher } = useReloadWatcherWriter();

  const { methods } = useReactHookForm<Group>({
    defaultValues: getDefaultValues(group),
    validationSchema: createGroupValidationSchema,
  });

  useEffect(() => {
    if (group) {
      methods.reset(getDefaultValues(group));
    }
  }, [group, methods]);

  const submitGroup = (values) => {
    const isEdit = values.id !== "";
    const saveGroup: AsyncCallArgs = {
      promise: saveGroupMainDetails(values),
      reactHooksForm: { methods },
      onSuccess: () => {
        if (!isEdit) {
          dispatch(closeModal({ modalType: "create" }));
          toast.success(t("m.group.create.success"));
        } else {
          updateReloadWatcher(ReloadWatcherObject.build("groupMainDetails", "change"));
          toast.success(t("m.group.update.success"));
        }
        filterFn();
      },
    };
    asyncCall(saveGroup);
  };

  return (
    <FormProvider {...methods}>
      <form
        style={{ width: "100%" }}
        onSubmit={methods.handleSubmit(submitGroup, () => handleSubmitValidationFail(methods.formState, t))}
      >
        <GridContainer spacing={3} mt={0}>
          <GridItem sm={6} md={6}>
            <InputFormField fieldName={"name"} labelCode={"l.name"} required={true}></InputFormField>
          </GridItem>
          <GridItem sm={6} md={6}>
            <InputFormField fieldName={"description"} labelCode={"l.description"}></InputFormField>
          </GridItem>
          <GridItem sm={6} md={6}>
            <ScrollableAsyncFormAutocomplete
              key={"parent-group-" + methods.getValues("id")}
              fieldName={"parentGroup.id"}
              selectedOption={methods.getValues("parentGroup")}
              setOptionText={(option) => option.name}
              getOptionLabel={(option) => option.id + ""}
              setInputOnSelect={(option) => {
                return option.name;
              }}
              autocompleteFn={getGroupsAutocomplete}
              label={"l.parentGroup"}
              additionalParams={{ excludedGroupId: methods.getValues("id") }}
            />
          </GridItem>
          <GridItem sm={12} md={12}>
            <Typography align={"right"}>
              <SubmitFormButton label={"l.btn.save"} />
            </Typography>
          </GridItem>
        </GridContainer>
      </form>
    </FormProvider>
  );
};

export default GroupMainDetailsForm;
